import { useEffect, useMemo } from "react"
import { useForm } from "react-hook-form"

import type { ProjectBudgetForm_project$data } from "./__generated__/ProjectBudgetForm_project.graphql"
import type { ProjectBudgetForm_user$data } from "./__generated__/ProjectBudgetForm_user.graphql"

import {
  OTHER_EXPENSES_ROLE,
  extractRoles,
  getTotalExpenses,
} from "~/helpers/otherCosts"
import { FinancialType } from "~/helpers/permissions"
import { sortByString } from "~/helpers/sorting-helpers"

import {
  transformFormDataToMutation,
  transformProjectToFormData,
} from "./formTransformations"
import {
  ProjectBudgetFormData,
  projectBudgetValidationRules,
} from "./formTypes"
import { getProjectTotalAllocation } from "./projectBudgetHelpers"
import {
  calculatePhaseRoleBudget,
  sumPhaseBudgets,
} from "~/helpers/project-helpers"
import { match } from "ts-pattern"

type UseProjectBudgetFormProps = {
  project: ProjectBudgetForm_project$data
  user: ProjectBudgetForm_user$data
}

export const useProjectBudgetForm = ({
  project,
  user,
}: UseProjectBudgetFormProps) => {
  const formMethods = useForm<ProjectBudgetFormData>({
    defaultValues: transformProjectToFormData(project, user.account),
    mode: "onChange",
    // Apply validation rules
    ...projectBudgetValidationRules,
  })

  const { watch, setValue, getValues } = formMethods

  // Watch specific fields for calculations
  const watchedData = watch()
  const {
    roles,
    projectRates,
    otherExpenses,
    totalBudget,
    phaseBudgets,
    noPhaseBudget,
    budgetMethod,
    pricingModel,
    rateType,
    phaseAndRoleBudgets,
  } = watchedData

  // Derived calculations
  const totalAllocated = useMemo(() => {
    return match(budgetMethod)
      .with("ROLE", () => getProjectTotalAllocation(roles, projectRates))
      .with("PHASE", () => {
        const totalPhaseBudget = sumPhaseBudgets(phaseBudgets)
        return totalPhaseBudget + noPhaseBudget
      })
      .with("PHASE_AND_ROLE", () => {
        return calculatePhaseRoleBudget(
          phaseAndRoleBudgets,
          project.rate_card,
          projectRates,
        )
      })
      .exhaustive()
  }, [
    budgetMethod,
    roles,
    projectRates,
    phaseAndRoleBudgets,
    phaseBudgets,
    noPhaseBudget,
    project.rate_card,
  ])

  const totalExpenses = useMemo(() => {
    return getTotalExpenses(otherExpenses)
  }, [otherExpenses])

  const budgetAllocationDifference = useMemo(() => {
    return totalBudget - totalAllocated
  }, [totalBudget, totalAllocated])

  const expensesAllocationDifference = useMemo(() => {
    const expenseRole = roles.find((r) => r.id === OTHER_EXPENSES_ROLE.id)
    return (expenseRole?.estimatedMinutes ?? 0) - totalExpenses.charge
  }, [roles, totalExpenses])

  // Check if there are actual expenses with costs or charges
  const hasExpenses = useMemo(() => {
    return otherExpenses.some((exp) => exp.charge > 0 || exp.cost > 0)
  }, [otherExpenses])

  // Check if we're in non-billable view
  const showNonBillableView = useMemo(() => {
    return pricingModel === "nb"
  }, [pricingModel])

  // Auto-management of "Other Expenses" role
  useEffect(() => {
    const currentRoles = getValues("roles")
    const hasExpenseRole = currentRoles.some(
      (r) => r.id === OTHER_EXPENSES_ROLE.id,
    )

    if (hasExpenses && !hasExpenseRole && !showNonBillableView) {
      setValue("roles", [
        ...currentRoles,
        {
          ...OTHER_EXPENSES_ROLE,
          estimatedMinutes: 0,
          projectRoleId: OTHER_EXPENSES_ROLE.id,
          implicit: true,
          isScheduled: true,
        },
      ])
    } else if (!hasExpenses && hasExpenseRole) {
      const expenseRole = currentRoles.find(
        (r) => r.id === OTHER_EXPENSES_ROLE.id,
      )
      if (expenseRole?.implicit && !expenseRole.estimatedMinutes) {
        setValue("expensesBudget", 0)
        setValue(
          "roles",
          currentRoles.filter((r) => r.id !== OTHER_EXPENSES_ROLE.id),
        )
      }
    } else if (showNonBillableView && hasExpenseRole) {
      setValue("expensesBudget", 0)
      setValue(
        "roles",
        currentRoles.filter((r) => r.id !== OTHER_EXPENSES_ROLE.id),
      )
    }
  }, [otherExpenses, showNonBillableView, hasExpenses, setValue, getValues])

  // Permission checks
  const financialPermission = user.permissions.financial as FinancialType
  const hideFinancials = financialPermission === FinancialType.None
  const hasFinancialPermissions = ["all", "restricted"].includes(
    financialPermission,
  )

  // Helper to determine if we should show the financial columns
  const showFinancialColumns = !hideFinancials && !showNonBillableView

  const showDayView = rateType === "days"

  // Transform form data for mutation
  const getMutationInput = () => {
    const formData = getValues()
    return transformFormDataToMutation(formData, hasFinancialPermissions)
  }

  const allRoles = useMemo(() => {
    const accountRoles = user.account.roles
      .filter((r) => r.active)
      .map((r) => {
        const existingBudgetedRole = project.project_roles.find(
          (pr) => r.id === pr.role_id,
        )?.id

        return {
          ...r,
          estimatedMinutes: 0,
          projectRoleId: existingBudgetedRole,
        }
      })

    // Add "Other Expenses" role if needed (only for role budget view, not phase + role)
    const isCompleteExpense = (expense) =>
      expense.name.trim() || expense.charge !== 0 || expense.cost !== 0

    const shouldShowOtherExpenses =
      budgetMethod === "ROLE" &&
      !extractRoles(roles).expenses &&
      !otherExpenses.filter(isCompleteExpense).length &&
      !showNonBillableView

    if (shouldShowOtherExpenses) {
      accountRoles.push({
        ...OTHER_EXPENSES_ROLE,
        estimatedMinutes: 0,
        projectRoleId: OTHER_EXPENSES_ROLE.id,
      })
    }

    // Sort roles alphabetically, but keep "Other Expenses" at the bottom
    return accountRoles.sort((a, b) => {
      if (a.id === OTHER_EXPENSES_ROLE.id) return 1
      if (b.id === OTHER_EXPENSES_ROLE.id) return -1
      return sortByString(a.name, b.name)
    })
  }, [
    user.account.roles,
    project.project_roles,
    roles,
    otherExpenses,
    showNonBillableView,
    budgetMethod,
  ])

  return {
    formMethods,
    // Calculated values
    totalAllocated,
    totalExpenses,
    budgetAllocationDifference,
    expensesAllocationDifference,
    hasExpenses,
    rateType,
    // UI things
    showNonBillableView,
    showFinancialColumns,
    showDayView,
    // Permission flags
    hideFinancials,
    hasFinancialPermissions,
    // Utility functions
    getMutationInput,
    // Categorized role data
    categorizedRoles: extractRoles(roles),
    allRoles,
  }
}
